/**
 * Entities Redux Slice
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { FilteredState, PaginationParams, SortParams, FilterParams } from '../types';
import { appConfig } from '@/config/app-config';
export interface Entity {
  id: number;
  code: string;
  name: string;
  description: string | null;
  type: string | null;
  status: string | null;
  avatar_url: string | null;
  is_active: boolean;
  created_at: string;
  created_by: string | null;
  updated_at: string | null;
  updated_by: string | null;
}
// Interfaz para opciones de filtro (usado en dropdowns)
export interface EntityFilterOption {
  value: string;
  label: string;
  avatar_url?: string;
  type?: string;
}
interface EntitiesState extends FilteredState<Entity> {
  selectedEntity: Entity | null;
  creating: boolean;
  updating: boolean;
  deleting: boolean;
  // Estado para opciones de filtro (cacheadas para evitar recargas)
  filterOptions: EntityFilterOption[];
  filterOptionsLoading: boolean;
  filterOptionsError: string | null;
  filterOptionsLastFetch: number | null;
}
const initialState: EntitiesState = {
  items: [],
  totalCount: 0,
  currentPage: 1,
  pageSize: 50,
  loading: false,
  error: null,
  lastFetch: null,
  filters: {},
  sortField: 'name',
  sortDirection: 'asc',
  searchTerm: null,
  selectedEntity: null,
  creating: false,
  updating: false,
  deleting: false,
  // Estado para opciones de filtro
  filterOptions: [],
  filterOptionsLoading: false,
  filterOptionsError: null,
  filterOptionsLastFetch: null,
};
// Thunk para cargar entities con paginación y filtros (para vistas de lista)
export const fetchEntities = createAsyncThunk<{ data: Entity[]; totalCount: number }, any>('entities/fetchEntities', async (params: any) => {
  throw new Error('Not implemented yet');
});
// Thunk para cargar opciones de filtro (solo id, nombre, avatar) - CACHEADO
export const fetchEntityFilterOptions = createAsyncThunk(
  'entities/fetchFilterOptions',
  async (_, { getState, rejectWithValue }) => {
    const state = getState() as any;
    const entitiesState = state.entities as EntitiesState;
    // ✅ CACHE: Si ya tenemos datos frescos (menos de 5 minutos), usar cache
    const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos
    if (
      entitiesState.filterOptionsLastFetch &&
      Date.now() - entitiesState.filterOptionsLastFetch < CACHE_DURATION &&
      entitiesState.filterOptions.length > 0
    ) {
      return entitiesState.filterOptions;
    }
    try {
      // Obtener token de autenticación
      const token = localStorage.getItem('authToken') || '';
      const url = appConfig.getApiUrl('masters/entities?startRow=0&endRow=500&sortProperty=name&sortDirection=asc');
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        const errorText = await response.text().catch(() => '');
        throw new Error(`Error ${response.status}: ${errorText}`);
      }
      const result = await response.json();
      const entities = result?.data?.rows || [];
      // Transformar a formato de opciones de filtro
      const filterOptions: EntityFilterOption[] = entities.map((entity: any) => ({
        value: entity.id.toString(),
        label: entity.name,
        avatar_url: entity.avatar_url,
        type: entity.type
      }));
      return filterOptions;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Error al cargar opciones de filtro');
    }
  }
);
export const fetchEntityById = createAsyncThunk('entities/fetchEntityById', async (id: number) => {
  throw new Error('Not implemented yet');
});
export const createEntity = createAsyncThunk('entities/createEntity', async (entity: Partial<Entity>) => {
  throw new Error('Not implemented yet');
});
export const updateEntity = createAsyncThunk('entities/updateEntity', async ({ id, data }: { id: number; data: Partial<Entity> }) => {
  throw new Error('Not implemented yet');
});
export const deleteEntity = createAsyncThunk('entities/deleteEntity', async (id: number) => {
  throw new Error('Not implemented yet');
});
const entitiesSlice = createSlice({
  name: 'entities',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<FilterParams>) => {
      state.filters = { ...state.filters, ...action.payload };
      state.currentPage = 1;
    },
    clearFilters: (state) => {
      state.filters = {};
      state.searchTerm = null;
      state.currentPage = 1;
    },
    setSearchTerm: (state, action: PayloadAction<string | null>) => {
      state.searchTerm = action.payload;
      state.currentPage = 1;
    },
    setSort: (state, action: PayloadAction<SortParams>) => {
      state.sortField = action.payload.field;
      state.sortDirection = action.payload.direction;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
      state.currentPage = 1;
    },
    selectEntity: (state, action: PayloadAction<Entity | null>) => {
      state.selectedEntity = action.payload;
    },
    clearSelection: (state) => {
      state.selectedEntity = null;
    },
  },
  extraReducers: (builder) => {
    // Casos para fetchEntities (lista completa)
    builder
      .addCase(fetchEntities.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEntities.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.data;
        state.totalCount = action.payload.totalCount;
        state.lastFetch = Date.now();
      })
      .addCase(fetchEntities.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Error al cargar entidades';
      })
      // Casos para fetchEntityFilterOptions (opciones cacheadas)
      .addCase(fetchEntityFilterOptions.pending, (state) => {
        state.filterOptionsLoading = true;
        state.filterOptionsError = null;
      })
      .addCase(fetchEntityFilterOptions.fulfilled, (state, action) => {
        state.filterOptionsLoading = false;
        state.filterOptions = action.payload;
        state.filterOptionsLastFetch = Date.now();
      })
      .addCase(fetchEntityFilterOptions.rejected, (state, action) => {
        state.filterOptionsLoading = false;
        state.filterOptionsError = action.payload as string || 'Error al cargar opciones de filtro';
      });
  },
});
// Selectores para acceder al estado
export const selectEntityFilterOptions = (state: any) => state.entities.filterOptions;
export const selectEntityFilterOptionsLoading = (state: any) => state.entities.filterOptionsLoading;
export const selectEntityFilterOptionsError = (state: any) => state.entities.filterOptionsError;
// Helper para verificar si las opciones de filtro están frescas
export const areEntityFilterOptionsFresh = (state: any): boolean => {
  const lastFetch = state.entities.filterOptionsLastFetch;
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutos
  return lastFetch && Date.now() - lastFetch < CACHE_DURATION;
};
export const { setFilters, clearFilters, setSearchTerm, setSort, setPage, setPageSize, selectEntity, clearSelection } = entitiesSlice.actions;
export default entitiesSlice.reducer;
